import React, { useEffect, useState, useRef, useMemo } from 'react';
import './DavidSpeechTranscript.css';

/**
 * DavidSpeechTranscript
 * Renders dynamic, live word-to-word time-synced subtitles directly on top of David's avatar.
 * Synchronized with the active HTML5 audio element's currentTime & duration.
 */
export function DavidSpeechTranscript({
  text = '',
  audioElement = null,
  isActive = false,
}) {
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const rafRef = useRef(null);

  const words = useMemo(() => {
    if (!text || typeof text !== 'string') return [];
    return text.trim().split(/\s+/).filter(Boolean);
  }, [text]);

  useEffect(() => {
    if (!text || !isActive || words.length === 0) {
      setActiveWordIndex(0);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const audio = audioElement;

    const tick = () => {
      if (!audio) {
        return;
      }

      if (audio.ended) {
        setActiveWordIndex(words.length - 1);
        return;
      }

      const duration = audio.duration;
      const currentTime = audio.currentTime;

      if (duration && duration > 0 && !isNaN(duration)) {
        const progress = Math.min(1, Math.max(0, currentTime / duration));
        const currentIdx = Math.min(words.length - 1, Math.floor(progress * words.length));
        setActiveWordIndex(currentIdx);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [text, audioElement, isActive, words.length]);

  if (!text || !isActive || words.length === 0) {
    return null;
  }

  return (
    <div className="david-speech-transcript-box" aria-live="polite">
      <div className="david-transcript-header">
        <div className="david-transcript-avatar-chip">
          <span className="david-transcript-dot" />
          <span className="david-transcript-name">David</span>
        </div>
      </div>
      <div className="david-transcript-body">
        {words.slice(0, activeWordIndex + 1).map((word, index) => {
          const isCurrent = index === activeWordIndex;

          return (
            <span
              key={`${index}-${word}`}
              className={`david-transcript-word ${
                isCurrent ? 'david-transcript-word--current' : 'david-transcript-word--spoken'
              }`}
            >
              {word}{' '}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default DavidSpeechTranscript;
